/**
 * Overlapping initials bubbles for the people behind a file, with a tooltip
 * naming each one ("Added by Tom Teammate", "Owner Priya Shah") on hover and
 * on keyboard focus. The group is one focusable control with one accessible
 * label, so a screen reader hears the names once.
 */
import React from "react";
import { describePerson, type FilePerson } from "../../utils/folderSharing";
import OwnerAvatar from "./OwnerAvatar";

interface PeopleBubblesProps {
	people: FilePerson[];
}

const PeopleBubbles: React.FC<PeopleBubblesProps> = React.memo(({ people }) => {
	if (people.length === 0) return null;
	const lines = people.map(describePerson);
	return (
		<span
			tabIndex={0}
			aria-label={lines.join(", ")}
			className="group/people relative z-10 inline-flex items-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#004C4B]"
		>
			<span className="flex -space-x-1.5">
				{people.map((person) => (
					<OwnerAvatar
						key={`${person.role}:${person.user.id}`}
						name={person.user.name}
						decorative
						className="ring-2 ring-white"
					/>
				))}
			</span>
			<span
				role="tooltip"
				className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-1.5 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-left text-xs font-medium text-white shadow-lg group-hover/people:block group-focus/people:block"
			>
				{lines.map((line) => (
					<span key={line} className="block">
						{line}
					</span>
				))}
			</span>
		</span>
	);
});
PeopleBubbles.displayName = "PeopleBubbles";

export default PeopleBubbles;
